import type {
  DomainResourceAction,
  DomainResourceTypeKey,
  OrganizationDomainResources,
} from '../../../types/models/OrganizationDomainResources';
import { formatDate } from '../../../utils/dateFormat';

/**
 * Registry of the resource types Ancalagon may host for an organization domain.
 *
 * This is the single source of truth for what the "Delete resources" wizard
 * (`/organization/domains/:id/delete`, `DomainDeleteResources.tsx`) offers. Any new resource type that
 * is hard-linked to an organization domain MUST be registered here so that a customer transferring
 * the domain away is always offered every linked resource. The registry is **keyed**
 * (`DOMAIN_RESOURCE_TYPE_BY_KEY`, a `Record` over `DomainResourceTypeKey`) so that adding a key to the
 * union is a compile error until its entry exists; the wizard's step-component map is keyed the same
 * way, so a new type also fails to compile until its step body exists.
 *
 * Entries start as `under_development`; each deletion mini-project flips its entry to `available`
 * when the corresponding enumeration/deletion behaviour ships. An `under_development` type is shown
 * in step 1 but cannot be selected.
 *
 * Design: `docs/plans/org-domain-delete-resources-wizard.md` §5.1.
 */
export type { DomainResourceTypeKey };

export type DomainResourceTypeStatus = 'under_development' | 'available';

export type DomainResourceElementKind =
  | 'dns_zone'
  | 'domain_registration'
  | 'mail_domain'
  | 'mailbox'
  | 'alias'
  | 'certificate';

/**
 * One selectable/reviewable unit inside a type, projected from the aggregate read.
 * `key` is unique across the whole wizard; `id` is the element's backend id.
 */
export interface DomainResourceElement {
  key: string;
  kind: DomainResourceElementKind;
  id: number;
  /** "info@example.com", "vpn.example.com (OPNsense trust)" */
  label: string;
  /** Secondary line (expiry, match, destination…); dates already formatted via `dateFormat.ts`. */
  detail: string | null;
  /** `false` ⇒ forced-kept: listed under *Will be kept* with `blockedReason`, never blocks the org domain. */
  deletable: boolean;
  blockedReason: string | null;
  /** `null` ⇔ `!deletable`. */
  action: DomainResourceAction | null;
  /**
   * A derived element has no checkbox: it is selected iff its dependencies are (today only the
   * mail-domain record, selected iff every deletable mailbox and alias is selected).
   */
  derived: boolean;
}

/** Everything a type needs to phrase the consequence of keeping some of its elements. */
export interface KeepConsequenceContext {
  domainName: string;
  /** Elements of this type that stay (kept by choice or forced-kept). */
  kept: readonly DomainResourceElement[];
  /** Every element of this type. */
  all: readonly DomainResourceElement[];
  /** Whether the DNS zone is going (some consequences depend on it, e.g. ZeroSSL auto-renewal). */
  dnsZoneGoes: boolean;
  resources: OrganizationDomainResources;
}

export interface DomainResourceTypeDefinition {
  label: string;
  description: string;
  status: DomainResourceTypeStatus;
  /** Atomic types have exactly one element (or none) and their wizard step is review-only. */
  atomic: boolean;
  /** Project the aggregate read into this type's elements; `[]` when nothing is hosted. Pure. */
  elementsOf: (resources: OrganizationDomainResources) => DomainResourceElement[];
  /** Consequence sentence shown under *Will be kept* for this type, or `null` when nothing is kept. Pure. */
  keepConsequence: (ctx: KeepConsequenceContext) => string | null;
}

export interface DomainResourceType extends DomainResourceTypeDefinition {
  key: DomainResourceTypeKey;
}

export const elementKey = (type: DomainResourceTypeKey, kind: DomainResourceElementKind, id: number): string =>
  `${type}:${kind}:${id}`;

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

const CERTIFICATE_MATCH_LABEL: Record<'exact' | 'wildcard' | 'subdomain', string> = {
  exact: 'exact match',
  wildcard: 'wildcard',
  subdomain: 'subdomain',
};

export const DOMAIN_RESOURCE_TYPE_BY_KEY: Record<DomainResourceTypeKey, DomainResourceTypeDefinition> = {
  dns_zone: {
    label: 'DNS zone',
    description: "The domain's DNS zone hosted on Ancalagon's nameservers, including all of its records.",
    // Deletion path: `DELETE /datacenters/domain-names/{id}/zone?delete_zone=1` — the zone is removed
    // from the endpoint and the binding cleared (`docs/api-types/domain-names.md`).
    status: 'available',
    atomic: true,
    elementsOf: (resources) => {
      const zone = resources.dns_zone;
      // No bridged domain_name row, or an unbound one (the backend then sends the whole family as null).
      if (zone === null) {
        return [];
      }
      let detail: string;
      if (zone.record_count !== null) {
        detail = `${plural(zone.record_count, 'record', 'records')}${zone.serial !== null ? ` · serial ${zone.serial}` : ''}`;
      } else if (zone.zone_state === 'unknown') {
        detail = 'The zone could not be read from its endpoint';
      } else {
        detail = 'No zone on the endpoint — only the binding is removed';
      }
      return [
        {
          key: elementKey('dns_zone', 'dns_zone', zone.domain_name_id),
          kind: 'dns_zone',
          id: zone.domain_name_id,
          label: `${resources.organization_domain.name} on ${zone.dns_endpoint_name ?? 'its DNS endpoint'}`,
          detail,
          deletable: zone.deletable,
          blockedReason: zone.blocked_reason,
          action: zone.action,
          derived: false,
        },
      ];
    },
    keepConsequence: ({ kept }) => (kept.length === 0 ? null : 'Keeping the DNS zone keeps the organization domain.'),
  },

  domain_registration: {
    label: 'Domain registration',
    description: 'The registrar-side registration record managed through Ancalagon, including its registrant contact.',
    // Deletion path: `DELETE /datacenters/domain-names/{id}` — unmanaged records only, and only once
    // the DNS zone is unbound (`docs/api-types/domain-names.md` → Delete Domain Name).
    status: 'available',
    atomic: true,
    elementsOf: (resources) => {
      const registration = resources.domain_registration;
      if (registration === null) {
        return [];
      }
      const registrar = registration.domain_registration_platform?.name ?? null;
      const detailParts: string[] = [];
      if (registrar !== null) {
        detailParts.push(`Registered through ${registrar}`);
      }
      if (registration.expires_at !== null) {
        detailParts.push(`expires on ${formatDate(registration.expires_at)}`);
      }
      if (registration.in_transfer) {
        detailParts.push(
          registration.transfer_expires_at !== null
            ? `transfer in progress until ${formatDate(registration.transfer_expires_at)}`
            : 'transfer in progress',
        );
      }
      return [
        {
          key: elementKey('domain_registration', 'domain_registration', registration.domain_name_id),
          kind: 'domain_registration',
          id: registration.domain_name_id,
          label: `Registration record of ${resources.organization_domain.name}`,
          detail: detailParts.length > 0 ? detailParts.join(' · ') : null,
          deletable: registration.deletable,
          blockedReason: registration.blocked_reason,
          action: registration.action,
          derived: false,
        },
      ];
    },
    keepConsequence: ({ kept }) =>
      kept.length === 0 ? null : 'Keeping the domain registration keeps the organization domain.',
  },

  email_hosting: {
    label: 'Email hosting',
    description: 'The mail domain with all of its mailboxes and aliases.',
    // Deletion path: the backend's mail-stack teardown (selective mailbox/alias deletion and the
    // guarded mail-domain cascade) — `docs/api-types/mail-domains.md` → Delete Mail Domain.
    status: 'available',
    atomic: false,
    elementsOf: (resources) => {
      const email = resources.email_hosting;
      if (email === null) {
        return [];
      }
      const domainName = resources.organization_domain.name;
      const mailboxes: DomainResourceElement[] = email.mailboxes.map((mailbox) => ({
        key: elementKey('email_hosting', 'mailbox', mailbox.id),
        kind: 'mailbox',
        id: mailbox.id,
        label: `${mailbox.user}@${domainName}`,
        detail: null,
        deletable: mailbox.deletable,
        blockedReason: mailbox.blocked_reason,
        action: mailbox.action,
        derived: false,
      }));
      const aliases: DomainResourceElement[] = email.aliases.map((alias) => ({
        key: elementKey('email_hosting', 'alias', alias.id),
        kind: 'alias',
        id: alias.id,
        label: alias.source === '@' ? `@${domainName} (catch-all)` : `${alias.source}@${domainName}`,
        detail: `→ ${alias.destination}`,
        deletable: alias.deletable,
        blockedReason: alias.blocked_reason,
        action: alias.action,
        derived: false,
      }));
      const hostDetail =
        email.host_state === 'reachable'
          ? `${email.mail_domain.imap_server_host ?? 'IMAP server'}${email.mail_domain.imap_server_mode !== null ? ` (${email.mail_domain.imap_server_mode})` : ''}`
          : email.host_state === 'stale_binding'
            ? 'No provisioning left on the mail host'
            : 'The mail host cannot be reached';
      // The mail-domain record is always one element when a mail_domain row exists — even with zero
      // mailboxes and aliases — so that the record itself stays deletable. It is derived: no checkbox,
      // selected iff every deletable mailbox and alias is selected.
      const record: DomainResourceElement = {
        key: elementKey('email_hosting', 'mail_domain', email.mail_domain.id),
        kind: 'mail_domain',
        id: email.mail_domain.id,
        label: `Mail domain ${domainName}`,
        detail: hostDetail,
        deletable: email.deletable,
        blockedReason: email.blocked_reason,
        action: email.action,
        derived: true,
      };
      return [...mailboxes, ...aliases, record];
    },
    keepConsequence: ({ kept, domainName }) => {
      const keptMailboxes = kept.filter((element) => element.kind === 'mailbox').length;
      const keptAliases = kept.filter((element) => element.kind === 'alias').length;
      if (keptMailboxes === 0 && keptAliases === 0) {
        return null;
      }
      const parts: string[] = [];
      if (keptMailboxes > 0) {
        parts.push(plural(keptMailboxes, 'mailbox', 'mailboxes'));
      }
      if (keptAliases > 0) {
        parts.push(plural(keptAliases, 'alias', 'aliases'));
      }
      return `Keeping ${parts.join(' and ')} keeps the mail domain ${domainName} and the organization domain.`;
    },
  },

  tls_certificates: {
    label: 'TLS certificates',
    description: 'Certificates issued for this domain and its subdomains.',
    status: 'available',
    atomic: false,
    elementsOf: (resources) =>
      resources.tls_certificates.certificates.map((certificate) => {
        const detailParts: string[] = [CERTIFICATE_MATCH_LABEL[certificate.match]];
        if (certificate.status === 'request') {
          detailParts.push('pending order');
        }
        if (certificate.not_after !== null) {
          detailParts.push(`expires on ${formatDate(certificate.not_after)}`);
        }
        return {
          key: elementKey('tls_certificates', 'certificate', certificate.id),
          kind: 'certificate',
          id: certificate.id,
          label: `${certificate.cn} (${certificate.platform_name})`,
          detail: detailParts.join(' · '),
          deletable: certificate.deletable,
          blockedReason: certificate.blocked_reason,
          action: certificate.action,
          derived: false,
        };
      }),
    keepConsequence: ({ kept, dnsZoneGoes, resources }) => {
      if (kept.length === 0) {
        return null;
      }
      const sentences: string[] = [
        `Keeping ${plural(kept.length, 'certificate', 'certificates')} keeps the organization domain.`,
      ];
      if (dnsZoneGoes) {
        const keptIds = new Set(kept.map((element) => element.id));
        const autoRenewing = resources.tls_certificates.certificates.filter(
          (certificate) => keptIds.has(certificate.id) && certificate.auto_renew,
        );
        if (autoRenewing.length > 0) {
          const expiries = autoRenewing
            .map((certificate) => certificate.not_after)
            .filter((value): value is string => value !== null)
            .map((value) => formatDate(value));
          sentences.push(
            `Deleting the DNS zone stops automatic renewal of ${plural(autoRenewing.length, 'kept ZeroSSL certificate', 'kept ZeroSSL certificates')}` +
              (expiries.length > 0
                ? `; ${expiries.length === 1 ? 'it expires' : 'they expire'} on ${expiries.join(', ')}.`
                : '.'),
          );
        }
      }
      return sentences.join(' ');
    },
  },
};

/** Display order of the types (step 1, the wizard steps, the review). A unit test asserts it lists every key once. */
export const DOMAIN_RESOURCE_TYPE_ORDER = [
  'dns_zone',
  'domain_registration',
  'email_hosting',
  'tls_certificates',
] as const satisfies readonly DomainResourceTypeKey[];

/** The registry as an ordered array — the shape consumed by the page, the helpers and the tests. */
export const DOMAIN_RESOURCE_TYPES: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPE_ORDER.map((key) => ({
  key,
  ...DOMAIN_RESOURCE_TYPE_BY_KEY[key],
}));
